import React, { useRef, useCallback, useMemo } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Loading from '../Loading';
import CustomTooltip from '../Tooltip';
import ReportComponent from '../../utils/ReportComponent/ReportComponent';
import config from '../../config';

const Report = ({ 
    data, 
    headerName, 
    loading, 
    order, 
    sort, 
    metrics, 
    keysMetric, 
    rowsCount,
    isSorting, 
    isTableFirst, 
    isTableLast,
    onAction, 
    t,
    bodyRef: externalBodyRef,
    tableWrapperClass = {}
}) => {
    const bodyRef = useRef(null);
    const actualBodyRef = externalBodyRef || bodyRef;

    const tableWrapperClasses = useMemo(() => ({
        'table-responsive': true,
        ...tableWrapperClass
    }), [tableWrapperClass]);

    const onUpdateSort = useCallback((event) => {
        let sortKey = event.target.closest('.report-table__th-sort').getAttribute('data-key');
        let newOrder = order;

        if (sortKey === sort) {
            newOrder = (order === 'desc') ? 'asc' : 'desc';
        } else {
            newOrder = 'desc';
        }

        onAction({
            sort: sortKey,
            order: newOrder,
            page: config.initialPage
        });
    }, [order, sort, onAction]);

    const setBodyData = useCallback(() => {
        if (actualBodyRef.current) {
            actualBodyRef.current.innerHTML = data || '';
        }
    }, [data, actualBodyRef]);

    React.useEffect(() => {
        setBodyData();
    }, [setBodyData]);

    const renderHeader = useCallback(() => {
        if (!headerName) return null;

        let groupByTitle = ReportComponent.utils.getGroupBy();
        return (
            <thead className="bold">
                {headerName.map((items, i) => {
                    return (
                        <tr key={i}>
                            {items.map((item, j) => {
                                let title = 'GroupBy' === item.config.key ? groupByTitle.title : item.title;
                                return (
                                    item.visible
                                        ? (
                                            <th
                                                className={`report-table__th`}
                                                key={j}
                                                colSpan={item.colspan}
                                                rowSpan={item.rowspan}
                                            >
                                                {item.description
                                                    ? (
                                                        <CustomTooltip id={`header-${i}-${j}`} tooltip={item.description}>
                                                            <span className="report-table__th-text">{title}</span>
                                                        </CustomTooltip>
                                                    )
                                                    : <span className="report-table__th-text">{title}</span>
                                                }
                                                {item.isSortable && isSorting && keysMetric && keysMetric.indexOf(item.config.key) !== -1
                                                    ? (
                                                        <span
                                                            className={`report-table__th-sort ${sort === item.config.key ? 'active': ''}`}
                                                            data-key={item.config.key}
                                                            onClick={onUpdateSort}
                                                        >
                                                            <i className={`fa fa-sort-amount-${order}`} />
                                                        </span>
                                                    )
                                                    : null
                                                }
                                            </th>
                                        )
                                        : null
                                )
                            })}
                        </tr>
                    )
                })}
            </thead>
        );
    }, [headerName, isSorting, keysMetric, sort, order, onUpdateSort]);

    const staticNamesTable = (t && t.dataTable) || {};
    const hasTableContent = Boolean(data && data.trim());
    const hasRows = typeof rowsCount === 'number' ? rowsCount > 0 : false;
    const showLoadingOverlay = loading && hasTableContent;
    const showCenteredLoader = loading && !hasTableContent;
    const showEmptyPlaceholder = !loading && !hasTableContent && !hasRows;

    return (
        <div className="loading-wrapper">
            {showLoadingOverlay && (
                <div className="loading-wrapper__item">
                    <Loading />
                </div>
            )}
            <div className={classNames(tableWrapperClasses)}>
                <table className={classNames({
                    'table': true,
                    'table-striped': false,
                    'table-bordered': false,
                    'report-table': true,
                    'green-hover': true,
                    'report-table_first': isTableFirst,
                    'report-table_last': isTableLast
                })}>
                    {renderHeader()}
                    <tbody ref={actualBodyRef} />
                </table>
                {showCenteredLoader && <Loading center />}
                {!showCenteredLoader && showEmptyPlaceholder && (
                    <div className="text-center table-notfound-sign">{staticNamesTable.noData}</div>
                )}
            </div>
        </div>
    );
};

Report.propTypes = {
    data: PropTypes.string,
    headerName: PropTypes.array,
    loading: PropTypes.bool,
    order: PropTypes.string,
    sort: PropTypes.string,
    metrics: PropTypes.string,
    keysMetric: PropTypes.array,
    rowsCount: PropTypes.number,
    isSorting: PropTypes.bool,
    isTableFirst: PropTypes.bool,
    isTableLast: PropTypes.bool,
    onAction: PropTypes.func,
    t: PropTypes.object,
    bodyRef: PropTypes.object,
    tableWrapperClass: PropTypes.object
};

Report.defaultProps = {
    data: '',
    headerName: [],
    loading: false,
    order: 'desc',
    sort: '',
    metrics: '',
    keysMetric: [],
    rowsCount: 0,
    isSorting: true,
    isTableFirst: false,
    isTableLast: false,
    onAction: () => {},
    tableWrapperClass: {}
};

export default withTranslation(Report);
